import React, { useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, FormGroup, Label, Input, Button, Alert, Badge, Progress, InputGroup, InputGroupText } from 'reactstrap';

export default function CostOfLivingCalculator() {
  const [formData, setFormData] = useState({
    location: 'sydney',
    familySize: '1',
    housingType: '',
    utilities: '',
    groceries: '',
    transportation: '',
    healthcare: '',
    entertainment: '',
    dining: '',
    childcare: '',
    education: '',
    insurance: '',
    other: ''
  });

  const [totalCost, setTotalCost] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const [monthlyIncome, setMonthlyIncome] = useState('');

  const cityMultipliers = {
    'sydney': { name: 'Sydney', multiplier: 1.0 },
    'melbourne': { name: 'Melbourne', multiplier: 0.95 },
    'brisbane': { name: 'Brisbane', multiplier: 0.85 },
    'perth': { name: 'Perth', multiplier: 0.88 },
    'adelaide': { name: 'Adelaide', multiplier: 0.80 },
    'canberra': { name: 'Canberra', multiplier: 0.92 },
    'hobart': { name: 'Hobart', multiplier: 0.75 },
    'darwin': { name: 'Darwin', multiplier: 0.90 }
  };

  const housingCosts = {
    'apartment-1br': { name: '1 Bedroom Apartment', base: 2000 },
    'apartment-2br': { name: '2 Bedroom Apartment', base: 2800 },
    'apartment-3br': { name: '3 Bedroom Apartment', base: 3500 },
    'house-2br': { name: '2 Bedroom House', base: 3200 },
    'house-3br': { name: '3 Bedroom House', base: 4000 },
    'house-4br': { name: '4 Bedroom House', base: 5000 },
    'shared': { name: 'Shared Accommodation', base: 800 }
  };

  const calculateCosts = () => {
    const cityMultiplier = cityMultipliers[formData.location].multiplier;
    let total = 0;
    let details = [];

    if (formData.housingType) {
      const housingCost = housingCosts[formData.housingType].base * cityMultiplier;
      total += housingCost;
      details.push({ category: 'Housing', amount: housingCost, icon: '🏠' });
    }
    if (formData.utilities) {
      const utilitiesCost = parseFloat(formData.utilities);
      total += utilitiesCost;
      details.push({ category: 'Utilities', amount: utilitiesCost, icon: '💡' });
    }
    if (formData.groceries) {
      const groceriesCost = parseFloat(formData.groceries);
      total += groceriesCost;
      details.push({ category: 'Groceries', amount: groceriesCost, icon: '🛒' });
    }
    if (formData.transportation) {
      const transportCost = parseFloat(formData.transportation);
      total += transportCost;
      details.push({ category: 'Transportation', amount: transportCost, icon: '🚗' });
    }
    if (formData.healthcare) {
      const healthcareCost = parseFloat(formData.healthcare);
      total += healthcareCost;
      details.push({ category: 'Healthcare', amount: healthcareCost, icon: '⚕️' });
    }
    if (formData.entertainment) {
      const entertainmentCost = parseFloat(formData.entertainment);
      total += entertainmentCost;
      details.push({ category: 'Entertainment', amount: entertainmentCost, icon: '🎬' });
    }
    if (formData.dining) {
      const diningCost = parseFloat(formData.dining);
      total += diningCost;
      details.push({ category: 'Dining Out', amount: diningCost, icon: '🍽️' });
    }
    if (formData.childcare) {
      const childcareCost = parseFloat(formData.childcare);
      total += childcareCost;
      details.push({ category: 'Childcare', amount: childcareCost, icon: '👶' });
    }
    if (formData.education) {
      const educationCost = parseFloat(formData.education);
      total += educationCost;
      details.push({ category: 'Education', amount: educationCost, icon: '📚' });
    }
    if (formData.insurance) {
      const insuranceCost = parseFloat(formData.insurance);
      total += insuranceCost;
      details.push({ category: 'Insurance', amount: insuranceCost, icon: '🛡️' });
    }
    if (formData.other) {
      const otherCost = parseFloat(formData.other);
      total += otherCost;
      details.push({ category: 'Other Expenses', amount: otherCost, icon: '📦' });
    }

    setTotalCost(total);
    setBreakdown(details);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReset = () => {
    setFormData({
      location: 'sydney', familySize: '1', housingType: '', utilities: '', groceries: '',
      transportation: '', healthcare: '', entertainment: '', dining: '', childcare: '',
      education: '', insurance: '', other: ''
    });
    setTotalCost(0);
    setBreakdown([]);
    setMonthlyIncome('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0
    }).format(amount);
  };

  const getFinancialHealth = () => {
    if (!monthlyIncome || totalCost === 0) return null;
    const income = parseFloat(monthlyIncome);
    const savings = income - totalCost;
    const savingsRate = (savings / income) * 100;
    
    if (savingsRate >= 20) return { status: 'excellent', color: 'success', message: 'Excellent! Your budget is very sustainable.' };
    if (savingsRate >= 10) return { status: 'good', color: 'info', message: 'Good! You have a healthy safety net.' };
    if (savingsRate >= 0) return { status: 'tight', color: 'warning', message: 'Tight budget. Keep an eye on extras.' };
    return { status: 'deficit', color: 'danger', message: 'Expenses exceed income. Review your costs.' };
  };

  const financialHealth = getFinancialHealth();

  // Theme Colors from KangarooEdu Style
  const colors = {
    primary: '#002147', // Deep Midnight Blue
    accent: '#f2a900',  // Golden Yellow
    lightBg: '#f8f9fa',
    mutedText: '#6c757d'
  };

  return (
    <div style={{ backgroundColor: colors.lightBg, minHeight: '100vh', fontFamily: "'Segoe UI', Roboto, sans-serif" }}>
      {/* Header Hero Section */}
      <div style={{ backgroundColor: "#7E1790", padding: '60px 0', marginBottom: '40px', borderBottom: `5px solid ${"#7E1790"}` }}>
        <Container>
          <Row className="align-items-center">
            <Col md={8}>
              <h1 className="display-5 fw-bold text-white mb-2">Cost of Living Calculator</h1>
              <p className="lead text-white-50 mb-0">Plan your journey to Australia with our comprehensive expense estimator.</p>
            </Col>
            {/* <Col md={4} className="text-md-end d-none d-md-block">
              <div style={{ fontSize: '5rem', opacity: 0.2 }}>🇦🇺</div>
            </Col> */}
          </Row>
        </Container>
      </div>

      <Container className="pb-5">
        <Row>
          <Col lg={8}>
            {/* Step 1: Context */}
            <Card className="shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
              <div style={{ height: '4px', backgroundColor: "#7E1790" }}></div>
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>1</div>
                  <h4 className="mb-0 fw-bold" style={{ color: colors.primary }}>Location & Household</h4>
                </div>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-uppercase text-muted">Select City</Label>
                      <Input type="select" name="location" value={formData.location} onChange={handleChange} className="form-select-lg border-2">
                        {Object.keys(cityMultipliers).map(city => (
                          <option key={city} value={city}>{cityMultipliers[city].name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label className="small fw-bold text-uppercase text-muted">Family Size</Label>
                      <Input type="select" name="familySize" value={formData.familySize} onChange={handleChange} className="form-select-lg border-2">
                        <option value="1">Single Person</option>
                        <option value="2">Couple</option>
                        <option value="3">Family of 3</option>
                        <option value="4">Family of 4</option>
                        <option value="5+">Family of 5+</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Step 2: Expenses */}
            <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
              <div style={{ height: '4px', backgroundColor: "#7E1790" }}></div>
              <CardBody className="p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>2</div>
                  <h4 className="mb-0 fw-bold" style={{ color: colors.primary }}>Expense Details (Monthly)</h4>
                </div>

                <div className="p-3 mb-4 rounded-3" style={{ backgroundColor: '#f0f4f8' }}>
                  <Label className="fw-bold mb-2">🏠 Accommodation</Label>
                  <Input type="select" name="housingType" value={formData.housingType} onChange={handleChange} className="form-select-lg mb-2">
                    <option value="">Choose housing style...</option>
                    {Object.keys(housingCosts).map(key => (
                      <option key={key} value={key}>{housingCosts[key].name}</option>
                    ))}
                  </Input>
                  {formData.housingType && (
                    <Badge color="light" text="dark" className="p-2">
                      Market Avg: {formatCurrency(housingCosts[formData.housingType].base * cityMultipliers[formData.location].multiplier)}
                    </Badge>
                  )}
                </div>

                <Row className="g-4">
                  {[
                    { label: 'Utilities', name: 'utilities', icon: '💡', hint: 'Gas, Water, Web' },
                    { label: 'Groceries', name: 'groceries', icon: '🛒', hint: 'Food & Household' },
                    { label: 'Insurance', name: 'insurance', icon: '🛡️', hint: 'Health & Car' },
                    { label: 'Dining Out', name: 'dining', icon: '🍽️', hint: 'Cafes & Bars' },
                    { label: 'Transport', name: 'transportation', icon: '🚗', hint: 'Fuel or Opal' },
                    { label: 'Healthcare', name: 'healthcare', icon: '⚕️', hint: 'Medical costs' },
                    { label: 'Education', name: 'education', icon: '📚', hint: 'Tuition/Fees' },
                    { label: 'Other', name: 'other', icon: '📦', hint: 'Miscellaneous' },
                  ].map((item) => (
                    <Col md={6} key={item.name}>
                      <FormGroup>
                        <Label className="small fw-bold text-muted">{item.icon} {item.label.toUpperCase()}</Label>
                        <InputGroup size="lg">
                          <InputGroupText className="bg-white border-end-0">$</InputGroupText>
                          <Input 
                            type="number" 
                            className="border-start-0" 
                            name={item.name} 
                            placeholder="0" 
                            value={formData[item.name]} 
                            onChange={handleChange} 
                          />
                        </InputGroup>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>{item.hint}</small>
                      </FormGroup>
                    </Col>
                  ))}
                </Row>

                <div className="mt-5 pt-4 border-top d-flex flex-column flex-md-row justify-content-between gap-3">
                  <Button outline color="secondary" size="lg" onClick={handleReset} className="px-5 border-2 fw-bold">Reset Form</Button>
                  <Button color="warning" size="lg" onClick={calculateCosts} className="px-5 fw-bold shadow-sm" style={{ backgroundColor: "#7E1790", border: 'none', color: '#000' }}>
                    Generate Analysis
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={4}>
            <div className="sticky-top" style={{ top: '20px', zIndex: 10 }}>
              {/* Result Summary Card */}
              <Card className="shadow-lg border-0 rounded-3 overflow-hidden mb-4 text-center">
                <CardHeader className="text-white py-4 border-0" style={{ backgroundColor: "#7E1790"}}>
                  <p className="text-uppercase mb-1 small fw-bold" style={{ letterSpacing: '2px', opacity: 0.8 }}>Total Estimate</p>
                  <h2 className="display-4 fw-bold mb-0">{formatCurrency(totalCost)}</h2>
                  <p className="mb-0 mt-2 small">Monthly in {cityMultipliers[formData.location].name}</p>
                </CardHeader>
                
                <CardBody className="p-4">
                  {totalCost > 0 ? (
                    <>
                      <FormGroup className="text-start mb-4">
                        <Label className="small fw-bold text-muted">YOUR MONTHLY INCOME (AUD)</Label>
                        <InputGroup>
                          <InputGroupText>$</InputGroupText>
                          <Input type="number" value={monthlyIncome} onChange={(e) => setMonthlyIncome(e.target.value)} placeholder="e.g. 5000" />
                        </InputGroup>
                      </FormGroup>

                      {financialHealth && (
                        <div className={`p-3 rounded-3 mb-4 bg-light text-start border-start border-4 border-${financialHealth.color}`}>
                          <div className="fw-bold d-flex justify-content-between">
                            Budget Status:
                            <Badge color={financialHealth.color}>{financialHealth.status.toUpperCase()}</Badge>
                          </div>
                          <small className="text-muted d-block mt-1">{financialHealth.message}</small>
                        </div>
                      )}

                      <div className="text-start">
                        <h6 className="fw-bold mb-3 border-bottom pb-2">Allocation Breakdown</h6>
                        {breakdown.map((item, idx) => {
                          const percent = ((item.amount / totalCost) * 100).toFixed(1);
                          return (
                            <div key={idx} className="mb-3">
                              <div className="d-flex justify-content-between small mb-1">
                                <span>{item.icon} {item.category}</span>
                                <span className="fw-bold text-primary">{percent}%</span>
                              </div>
                              <Progress value={percent} style={{ height: '6px' }} color="primary" className="rounded-pill" />
                            </div>
                          );
                        })}
                        <div className="bg-light p-3 rounded mt-4 text-center">
                          <p className="mb-0 small text-muted">Estimated Annual Cost</p>
                          <h4 className="fw-bold text-primary mb-0">{formatCurrency(totalCost * 12)}</h4>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="py-5">
                      <div className="bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: '80px', height: '80px', fontSize: '2rem' }}>📊</div>
                      <p className="text-muted">Fill in the expense details to see your financial summary and breakdown.</p>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Disclaimer Tip */}
              <div className="p-3 rounded-3 text-center border-dashed" style={{ border: '2px dashed #dee2e6', backgroundColor: '#fff' }}>
                <p className="mb-0 small text-muted">
                  <strong>Disclaimer:</strong> These are estimates based on average Australian market data. Actual costs vary significantly by suburb and lifestyle.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}